import { useEffect, useMemo } from 'react';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { type EntityType } from '@/types/entity';
import { type Pagination } from '@/types';
import { useRouter } from 'next/router';

export const DOTS = '...';

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const usePagination = ({ totalCount, pageSize, siblingCount = 1, currentPage }: Pagination) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Pages count = siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    // If the number of pages is less than the page numbers we want to show in our paginationComponent, we return the range [1..totalPageCount]
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    // Exclude dots when only one position is left after/before the left/right page count to maintain Pagination component size
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};

export const useDataTablePagination = (tableEntity: EntityType) => {
  const router = useRouter();
  const { query, replace, pathname } = router;
  const { getPagination, hydratePaginationFromQuery } = useTableStore();

  useEffect(() => {
    hydratePaginationFromQuery(tableEntity, query);
  }, [tableEntity, query, hydratePaginationFromQuery]);

  const { pageSize, pageIndex, sortOrder, sortValue } = getPagination(tableEntity);

  const setQueryParams = (newParams: object) => {
    const newQuery = {
      ...query,
      ...newParams,
    };

    void replace(
      {
        pathname: pathname === '/' ? '/browse' : pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  return { offset: pageSize * pageIndex, limit: pageSize, pageIndex, sortOrder, sortValue, setQueryParams };
};
